import { useState, useEffect } from 'react';
import { PricingPlan } from '../contexts/PricingContext';
import './HowItWorksPlayer.css';

interface Step {
  id: number;
  title: string;
  emoji: string;
  elements: StepElement[];
}

interface PackageCard {
  name: string;
  price: string;
  buttonText?: string;
}

interface StepElement {
  type: 'globe' | 'cards' | 'input' | 'crypto' | 'dashboard' | 'launch';
  content?: string;
  packages?: PackageCard[];
}

interface VideoPlayerConfig {
  title: string;
  steps: Step[];
  colors: {
    background: string;
    backgroundGradient: string;
    primary: string;
    secondary: string;
    accent: string;
    textGlow: string;
  };
  timing: {
    stepDuration: number;
    transitionDuration: number;
  };
  text: {
    titleSize: string;
    stepSize: string;
  };
}

interface HowItWorksPlayerProps {
  config: VideoPlayerConfig;
  pricingPlans?: PricingPlan[];
}

export default function HowItWorksPlayer({ config, pricingPlans }: HowItWorksPlayerProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  const totalSteps = config.steps.length;
  const totalDuration = totalSteps * (config.timing.stepDuration + config.timing.transitionDuration);

  useEffect(() => {
    if (!isPlaying) return;

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % totalSteps);
    }, config.timing.stepDuration);

    return () => clearInterval(stepInterval);
  }, [isPlaying, totalSteps, config.timing.stepDuration]);

  useEffect(() => {
    if (!isPlaying) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (totalDuration / 100));
        return newProgress >= 100 ? 0 : newProgress;
      });
      setElapsedTime((prev) => (prev + 0.1) % totalDuration);
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isPlaying, totalDuration]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const currentStepData = config.steps[currentStep];

  return (
    <div 
      className="hiw-container"
      style={{
        '--hiw-bg-color': config.colors.background,
        '--hiw-bg-gradient': config.colors.backgroundGradient,
        '--hiw-primary-color': config.colors.primary,
        '--hiw-secondary-color': config.colors.secondary,
        '--hiw-accent-color': config.colors.accent,
        '--hiw-text-glow': config.colors.textGlow,
        '--hiw-title-size': config.text.titleSize,
        '--hiw-step-size': config.text.stepSize,
      } as React.CSSProperties}
      id="how-it-works"
    >
      <h1 className="hiw-title" data-testid="text-title">{config.title}</h1>
      
      <div className="hiw-player-wrapper">
        <div 
          className="hiw-video-frame" 
          data-testid="container-video-frame"
          onClick={togglePlayPause}
          style={{ cursor: 'pointer' }}
        >
          {!isPlaying && (
            <div className="hiw-pause-overlay" onClick={togglePlayPause}>
              <div className="hiw-pause-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="38" stroke="currentColor" strokeWidth="3" opacity="0.8"/>
                  <path d="M30 25L55 40L30 55V25Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="hiw-pause-text">Click to Play</div>
            </div>
          )}
          <div className="hiw-step-content">
            <h2 
              className="hiw-step-title" 
              data-testid={`text-step-${currentStep}`}
              key={`title-${currentStep}`}
            >
              {currentStepData.title}
            </h2>
            
            <div className="hiw-step-visual" key={`visual-${currentStep}`}>
              {currentStepData.elements.map((element, idx) => (
                <div key={idx} className={`hiw-element hiw-element-${element.type}`}>
                  {element.type === 'globe' && (
                    <div className="hiw-globe">
                      <div className="hiw-globe-core">🌍</div>
                      <div className="hiw-flag hiw-flag-1">🇺🇸</div>
                      <div className="hiw-flag hiw-flag-2">🇬🇧</div>
                      <div className="hiw-flag hiw-flag-3">🇨🇦</div>
                    </div>
                  )}
                  
                  {element.type === 'cards' && (
                    <div className="hiw-pricing-cards">
                      {pricingPlans && pricingPlans.length > 0 ? (
                        pricingPlans.slice(0, 3).map((plan, index) => (
                          <div key={plan.id} className={`hiw-card hiw-card-${(index % 3) + 1}`}>
                            <div className="hiw-card-title">{plan.name}</div>
                            <div className="hiw-card-price">
                              {plan.priceDisplay ||
                                plan.setupFee?.amount ||
                                plan.subscription?.amount ||
                                "$0"}
                            </div>
                            <div className="hiw-card-button">{plan.button || "Choose Plan"}</div>
                          </div>
                        ))
                      ) : element.packages && element.packages.length > 0 ? (
                        element.packages.map((pkg, index) => (
                          <div key={index} className={`hiw-card hiw-card-${index + 1}`}>
                            <div className="hiw-card-title">{pkg.name}</div>
                            <div className="hiw-card-price">{pkg.price}</div>
                            <div className="hiw-card-button">{pkg.buttonText || "Choose Plan"}</div>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="hiw-card hiw-card-1">
                            <div className="hiw-card-title">Starter</div>
                            <div className="hiw-card-price">$99</div>
                            <div className="hiw-card-button">Choose Plan</div>
                          </div>
                          <div className="hiw-card hiw-card-2">
                            <div className="hiw-card-title">Pro</div>
                            <div className="hiw-card-price">$299</div>
                            <div className="hiw-card-button">Choose Plan</div>
                          </div>
                          <div className="hiw-card hiw-card-3">
                            <div className="hiw-card-title">Enterprise</div>
                            <div className="hiw-card-price">$999</div>
                            <div className="hiw-card-button">Choose Plan</div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {element.type === 'input' && (
                    <div className="hiw-input-container">
                      <div className="hiw-input-label">Casino Name</div>
                      <div className="hiw-input-field">
                        <span className="hiw-typing-text">{element.content || 'LuckyDice.io'}</span>
                        <span className="hiw-cursor">|</span>
                      </div>
                      <div className="hiw-confetti">
                        <div className="hiw-confetti-piece"></div>
                        <div className="hiw-confetti-piece"></div>
                        <div className="hiw-confetti-piece"></div>
                        <div className="hiw-confetti-piece"></div>
                      </div>
                    </div>
                  )}
                  
                  {element.type === 'crypto' && (
                    <div className="hiw-crypto-container">
                      <div className="hiw-crypto-icon hiw-crypto-btc">₿</div>
                      <div className="hiw-crypto-icon hiw-crypto-eth">Ξ</div>
                      <div className="hiw-crypto-icon hiw-crypto-usdt">₮</div>
                      <div className="hiw-crypto-checkmark">✓</div>
                    </div>
                  )}
                  
                  {element.type === 'dashboard' && (
                    <div className="hiw-dashboard">
                      <div className="hiw-dashboard-panel hiw-panel-1">
                        <div className="hiw-panel-header">Themes</div>
                        <div className="hiw-color-swatches">
                          <div className="hiw-swatch hiw-swatch-1"></div>
                          <div className="hiw-swatch hiw-swatch-2"></div>
                          <div className="hiw-swatch hiw-swatch-3"></div>
                        </div>
                      </div>
                      <div className="hiw-dashboard-panel hiw-panel-2">
                        <div className="hiw-panel-header">Games</div>
                        <div className="hiw-game-grid">
                          <div className="hiw-game-thumb"></div>
                          <div className="hiw-game-thumb"></div>
                          <div className="hiw-game-thumb"></div>
                          <div className="hiw-game-thumb"></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {element.type === 'launch' && (
                    <div className="hiw-launch-container">
                      <div className="hiw-rocket">🚀</div>
                      <div className="hiw-sparkle hiw-sparkle-1">✨</div>
                      <div className="hiw-sparkle hiw-sparkle-2">✨</div>
                      <div className="hiw-sparkle hiw-sparkle-3">✨</div>
                      <div className="hiw-sparkle hiw-sparkle-4">✨</div>
                      <div className="hiw-success-text">Your Casino is Live!</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="hiw-controls" onClick={(e) => e.stopPropagation()}>
            <button 
              className="hiw-play-button" 
              onClick={(e) => {
                e.stopPropagation();
                togglePlayPause();
              }}
              data-testid="button-play-pause"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>
            
            <div className="hiw-progress-container">
              <div className="hiw-progress-bar">
                <div 
                  className="hiw-progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="hiw-time" data-testid="text-time">
              {formatTime(elapsedTime)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
